"use client";

import { useState, useMemo } from "react";
import Image from "next/image";
import {
  Search,
  Copy,
  Check,
  ExternalLink,
  Layers,
  Terminal,
  Grid3X3,
  LayoutGrid,
  Compass,
  Lightbulb,
  ChevronDown,
  ChevronUp,
  ArrowRight,
  CheckSquare,
  Activity,
  AppWindow,
  Columns,
  Table,
  LayoutTemplate,
  SlidersHorizontal,
  X,
  Sparkles,
  ShieldCheck,
} from "lucide-react";

interface ComponentLib {
  name: string;
  url: string;
  image: string;
  packageName: string;
  installCommand?: string;
  badge: string;
  paradigm: "copy-paste" | "headless" | "suite" | "web-components" | "visuals";
  categories: (
    | "popular"
    | "tailwind"
    | "headless"
    | "vue-svelte"
    | "copy-paste"
    | "suite"
    | "web-components"
    | "visuals"
  )[];
  features: {
    hasLayout: boolean; // Hat ein integriertes Layout-/Grid-System
    hasSkeleton: boolean; // Bringt Skeleton-Loader mit
    a11yFocus: boolean; // Ausgeprägter Barrierefreiheits-Fokus (BITV / WAI-ARIA First)
    isTailwindNative: boolean; // Für Tailwind CSS optimiert
    hasAnimation?: boolean; // Besondere Animationen/Visuals
  };
  frameworks: ("react" | "vue" | "svelte" | "solid" | "agnostic")[];
  tags: string[];
  description: string;
  brandGradient?: string;
}

const COMPONENT_LIBS: ComponentLib[] = [
  {
    name: "shadcn/ui",
    url: "https://ui.shadcn.com",
    image: "https://raw.githubusercontent.com/shadcn-ui/ui/main/apps/www/public/og.jpg",
    packageName: "shadcn@latest",
    installCommand: "pnpm dlx shadcn@latest init",
    badge: "React & Tailwind",
    paradigm: "copy-paste",
    categories: ["popular", "tailwind", "copy-paste"],
    features: {
      hasLayout: true,
      hasSkeleton: true,
      a11yFocus: true,
      isTailwindNative: true,
    },
    frameworks: ["react"],
    tags: ["React", "Tailwind CSS", "Radix UI", "Copy-Paste"],
    description:
      "Wunderschön gestaltete, hochgradig anpassbare Komponenten zum direkten Kopieren in eigene Apps. Volle Code-Kontrolle ohne NPM-Abhängigkeitsketten.",
    brandGradient: "from-zinc-900 via-neutral-900 to-black",
  },
  {
    name: "Radix UI",
    url: "https://www.radix-ui.com",
    image: "https://www.radix-ui.com/social/themes.png",
    packageName: "@radix-ui/react-dialog",
    badge: "Headless Primitives",
    paradigm: "headless",
    categories: ["popular", "headless"],
    features: {
      hasLayout: false,
      hasSkeleton: false,
      a11yFocus: true,
      isTailwindNative: false,
    },
    frameworks: ["react"],
    tags: ["React", "Headless", "WAI-ARIA", "Unstyled"],
    description:
      "Ungestylte, vollständig barrierefreie Primitives für anspruchsvolle Design-Systeme und Webanwendungen in React.",
    brandGradient: "from-violet-950 via-slate-900 to-black",
  },
  {
    name: "Material UI (MUI)",
    url: "https://mui.com",
    image: "https://mui.com/edge-functions/og-image?product=Material%20UI",
    packageName: "@mui/material",
    badge: "Enterprise Standard",
    paradigm: "suite",
    categories: ["popular", "suite"],
    features: {
      hasLayout: true,
      hasSkeleton: true,
      a11yFocus: true,
      isTailwindNative: false,
    },
    frameworks: ["react"],
    tags: ["React", "Material Design", "Enterprise", "Theming"],
    description:
      "Das meistgenutzte React-Komponentenframework basierend auf Google Material Design mit reichem Komponenten-Ökosystem und Design-Tokens.",
    brandGradient: "from-sky-900 via-blue-950 to-neutral-950",
  },
  {
    name: "Mantine",
    url: "https://mantine.dev",
    image: "https://raw.githubusercontent.com/mantinedev/mantine/master/.demo/mantine-og.png",
    packageName: "@mantine/core",
    badge: "Full-Featured",
    paradigm: "suite",
    categories: ["popular", "suite"],
    features: {
      hasLayout: true,
      hasSkeleton: true,
      a11yFocus: true,
      isTailwindNative: false,
    },
    frameworks: ["react"],
    tags: ["React", "100+ Komponenten", "Hooks", "Dark Mode"],
    description:
      "Umfassende React-Bibliothek mit über 100 vorkonfigurierten Komponenten, nativer Dark-Mode-Unterstützung und 50+ praktischen Hooks.",
    brandGradient: "from-blue-900 via-cyan-950 to-slate-950",
  },
  {
    name: "Chakra UI",
    url: "https://chakra-ui.com",
    image: "https://chakra-ui.com/og-image.png",
    packageName: "@chakra-ui/react",
    badge: "Composable UI",
    paradigm: "suite",
    categories: ["popular", "suite"],
    features: {
      hasLayout: true,
      hasSkeleton: true,
      a11yFocus: true,
      isTailwindNative: false,
    },
    frameworks: ["react"],
    tags: ["React", "Modular", "Accessible", "Style Props"],
    description:
      "Einfache, modulare und zugängliche Komponenten für React mit flexiblen Style Props zur schnellen Erstellung konsistenter Oberflächen.",
    brandGradient: "from-teal-900 via-emerald-950 to-neutral-950",
  },
  {
    name: "HeroUI (vormals NextUI)",
    url: "https://heroui.com",
    image: "https://heroui.com/opengraph-image.png",
    packageName: "@heroui/react",
    badge: "Modern & Sleek",
    paradigm: "copy-paste",
    categories: ["popular", "tailwind", "copy-paste"],
    features: {
      hasLayout: true,
      hasSkeleton: true,
      a11yFocus: true,
      isTailwindNative: true,
      hasAnimation: true,
    },
    frameworks: ["react"],
    tags: ["React", "Next.js", "Tailwind CSS", "Framer Motion"],
    description:
      "Ästhetische, blitzschnelle React-UI-Bibliothek auf Tailwind-CSS-Basis mit flüssigen Animationen und durchdachtem Farbkonzept.",
    brandGradient: "from-neutral-900 via-pink-950 to-black",
  },
  {
    name: "Ark UI",
    url: "https://ark-ui.com",
    image: "https://ark-ui.com/og-image.png",
    packageName: "@ark-ui/react",
    badge: "Universal Headless",
    paradigm: "headless",
    categories: ["headless"],
    features: {
      hasLayout: false,
      hasSkeleton: false,
      a11yFocus: true,
      isTailwindNative: false,
    },
    frameworks: ["react", "vue", "solid"],
    tags: ["Headless", "State Machines", "React", "Vue", "Solid"],
    description:
      "Framework-unabhängige, barrierefreie Primitives für React, Vue und Solid, angetrieben von deterministischen State Machines (Zag.js).",
    brandGradient: "from-amber-950 via-orange-950 to-black",
  },
  {
    name: "Tremor",
    url: "https://tremor.so",
    image: "https://tremor.so/social.png",
    packageName: "@tremor/react",
    badge: "Dashboards & KPIs",
    paradigm: "visuals",
    categories: ["popular", "tailwind", "visuals"],
    features: {
      hasLayout: true,
      hasSkeleton: true,
      a11yFocus: false,
      isTailwindNative: true,
    },
    frameworks: ["react"],
    tags: ["React", "Tailwind CSS", "Charts", "Analytics"],
    description:
      "Spezialisierte React-Komponenten zum schnellen Erstellen von modernen Dashboards mit ansprechenden Diagrammen und KPI-Karten.",
    brandGradient: "from-indigo-950 via-blue-950 to-black",
  },
  {
    name: "daisyUI",
    url: "https://daisyui.com",
    image: "https://img.daisyui.com/images/daisyui-logo/daisyui-cover.svg",
    packageName: "daisyui",
    installCommand: "pnpm add -D daisyui@latest",
    badge: "Pure Tailwind CSS",
    paradigm: "suite",
    categories: ["popular", "tailwind", "suite"],
    features: {
      hasLayout: true,
      hasSkeleton: true,
      a11yFocus: false,
      isTailwindNative: true,
    },
    frameworks: ["agnostic", "react", "vue", "svelte"],
    tags: ["Tailwind Plugin", "Pure CSS", "Themes", "No JS"],
    description:
      "Das populärste Komponenten-Plugin für Tailwind CSS. Bringt semantische CSS-Klassennamen für Buttons, Modals und Navbars ganz ohne JavaScript.",
    brandGradient: "from-emerald-950 via-teal-950 to-black",
  },
  {
    name: "Headless UI",
    url: "https://headlessui.com",
    image: "https://headlessui.com/opengraph-image.png",
    packageName: "@headlessui/react",
    badge: "Official Tailwind",
    paradigm: "headless",
    categories: ["tailwind", "headless"],
    features: {
      hasLayout: false,
      hasSkeleton: false,
      a11yFocus: true,
      isTailwindNative: true,
    },
    frameworks: ["react", "vue"],
    tags: ["Tailwind Labs", "Headless", "React & Vue", "Accessible"],
    description:
      "Offizielle, barrierefreie und ungestylte UI-Komponenten von Tailwind Labs für nahtlose Integration mit Tailwind CSS.",
    brandGradient: "from-cyan-950 via-slate-900 to-black",
  },
  {
    name: "Park UI",
    url: "https://park-ui.com",
    image: "https://park-ui.com/og.png",
    packageName: "@park-ui/react",
    badge: "Ark + Styling",
    paradigm: "copy-paste",
    categories: ["tailwind", "headless", "copy-paste"],
    features: {
      hasLayout: false,
      hasSkeleton: true,
      a11yFocus: true,
      isTailwindNative: true,
    },
    frameworks: ["react", "vue", "solid"],
    tags: ["Ark UI", "Panda CSS", "Tailwind", "State Machines"],
    description:
      "Elegante Komponenten auf Basis von Ark UI und Panda CSS / Tailwind CSS, die robuste Zustandsmaschinen mit modernem Styling vereinen.",
    brandGradient: "from-amber-950 via-neutral-900 to-black",
  },
  {
    name: "PrimeVue",
    url: "https://primevue.org",
    image: "https://primefaces.org/cdn/primevue/images/primevue-logo.png",
    packageName: "primevue",
    badge: "Vue 3 Enterprise",
    paradigm: "suite",
    categories: ["popular", "vue-svelte", "suite"],
    features: {
      hasLayout: true,
      hasSkeleton: true,
      a11yFocus: true,
      isTailwindNative: true,
    },
    frameworks: ["vue"],
    tags: ["Vue 3", "90+ Widgets", "Tailwind Mode", "Enterprise"],
    description:
      "Das umfassendste UI-Framework für Vue.js mit über 90 UI-Komponenten, Tailwind-kompatiblem Unstyled-Modus und Design-System-Unterstützung.",
    brandGradient: "from-emerald-950 via-green-950 to-neutral-950",
  },
  {
    name: "Vuetify",
    url: "https://vuetifyjs.com",
    image: "https://cdn.vuetifyjs.com/docs/images/logos/vuetify-logo-v3-slim-text-dark.svg",
    packageName: "vuetify",
    badge: "Vue Material",
    paradigm: "suite",
    categories: ["popular", "vue-svelte", "suite"],
    features: {
      hasLayout: true,
      hasSkeleton: true,
      a11yFocus: true,
      isTailwindNative: false,
    },
    frameworks: ["vue"],
    tags: ["Vue 3", "Material Design", "SSR Ready", "Ecosystem"],
    description:
      "Reife Vue-UI-Bibliothek mit detailgetreuen Material-Design-Komponenten, umfassender Konfiguration und herausragendem Tooling.",
    brandGradient: "from-blue-950 via-indigo-950 to-black",
  },
  {
    name: "Nuxt UI",
    url: "https://ui.nuxt.com",
    image: "https://ui.nuxt.com/assets/images/og.png",
    packageName: "@nuxt/ui",
    badge: "Nuxt Native",
    paradigm: "suite",
    categories: ["tailwind", "vue-svelte", "suite"],
    features: {
      hasLayout: true,
      hasSkeleton: true,
      a11yFocus: true,
      isTailwindNative: true,
    },
    frameworks: ["vue"],
    tags: ["Nuxt 3", "Vue", "Tailwind CSS", "Reka UI"],
    description:
      "Das offizielle, blitzschnelle UI-System für Nuxt und Vue, gebaut auf Tailwind CSS und barrierefreien Headless-Primitives.",
    brandGradient: "from-emerald-950 via-teal-950 to-neutral-950",
  },
  {
    name: "Skeleton",
    url: "https://skeleton.dev",
    image: "https://skeleton.dev/og-image.png",
    packageName: "@skeletonlabs/skeleton",
    installCommand: "pnpm add -D @skeletonlabs/skeleton",
    badge: "SvelteKit & Tailwind",
    paradigm: "suite",
    categories: ["tailwind", "vue-svelte", "suite"],
    features: {
      hasLayout: true,
      hasSkeleton: true,
      a11yFocus: true,
      isTailwindNative: true,
    },
    frameworks: ["svelte"],
    tags: ["SvelteKit", "Tailwind CSS", "Design Tokens", "Adaptive"],
    description:
      "Umfassendes UI-Toolkit für Svelte und Tailwind CSS mit starkem Fokus auf Design-Tokens, Animationen und responsive Layouts.",
    brandGradient: "from-rose-950 via-purple-950 to-black",
  },
  {
    name: "Bits UI",
    url: "https://bits-ui.com",
    image: "https://bits-ui.com/og.png",
    packageName: "bits-ui",
    badge: "Svelte Headless",
    paradigm: "headless",
    categories: ["headless", "vue-svelte"],
    features: {
      hasLayout: false,
      hasSkeleton: false,
      a11yFocus: true,
      isTailwindNative: false,
    },
    frameworks: ["svelte"],
    tags: ["Svelte", "Headless", "Radix Alternative", "Accessible"],
    description:
      "Sammlung ungestylter, barrierefreier Primitives für Svelte – das Radix-UI-Pendant für maximale Flexibilität im Svelte-Ökosystem.",
    brandGradient: "from-orange-950 via-amber-950 to-neutral-950",
  },
  {
    name: "Melt UI",
    url: "https://melt-ui.com",
    image: "https://melt-ui.com/og.png",
    packageName: "@melt-ui/svelte",
    badge: "Svelte Builders",
    paradigm: "headless",
    categories: ["headless", "vue-svelte"],
    features: {
      hasLayout: false,
      hasSkeleton: false,
      a11yFocus: true,
      isTailwindNative: false,
    },
    frameworks: ["svelte"],
    tags: ["Svelte", "Headless Builders", "Accessible", "Flexible"],
    description:
      "Moderne, ungestylte Builder für Svelte mit voller WAI-ARIA-Konformität und vollständiger Kontrolle über das gerenderte Markup.",
    brandGradient: "from-pink-950 via-rose-950 to-black",
  },
  {
    name: "Shoelace",
    url: "https://shoelace.style",
    image: "https://shoelace.style/assets/images/og-image.png",
    packageName: "@shoelace-style/shoelace",
    badge: "Web Components",
    paradigm: "web-components",
    categories: ["headless", "web-components"],
    features: {
      hasLayout: false,
      hasSkeleton: true,
      a11yFocus: true,
      isTailwindNative: false,
    },
    frameworks: ["agnostic"],
    tags: ["Custom Elements", "Framework-Agnostic", "Accessible", "CSS Vars"],
    description:
      "Zukunftssichere, framework-unabhängige Web-Components (jetzt Web Awesome), die in React, Vue, Svelte oder reinem HTML identisch funktionieren.",
    brandGradient: "from-sky-950 via-blue-950 to-neutral-950",
  },
  {
    name: "Ant Design",
    url: "https://ant.design",
    image: "https://gw.alipayobjects.com/zos/rmsportal/KDpgvguMpGfqaHPjicRK.svg",
    packageName: "antd",
    badge: "Enterprise Heavyweight",
    paradigm: "suite",
    categories: ["popular", "suite"],
    features: {
      hasLayout: true,
      hasSkeleton: true,
      a11yFocus: true,
      isTailwindNative: false,
    },
    frameworks: ["react"],
    tags: ["React", "Enterprise", "TypeScript", "Design System"],
    description:
      "Design-Sprache und Komponenten-Schwergewicht für komplexe Unternehmensanwendungen und Backoffices mit erstklassiger TypeScript-Unterstützung.",
    brandGradient: "from-blue-950 via-indigo-950 to-slate-950",
  },
  {
    name: "Ariakit",
    url: "https://ariakit.org",
    image: "https://ariakit.org/assets/images/og-image.png",
    packageName: "@ariakit/react",
    badge: "Accessibility First",
    paradigm: "headless",
    categories: ["headless"],
    features: {
      hasLayout: false,
      hasSkeleton: false,
      a11yFocus: true,
      isTailwindNative: false,
    },
    frameworks: ["react"],
    tags: ["React", "Headless", "WAI-ARIA", "Keyboard First"],
    description:
      "Plattform für barrierefreie React-Webapps mit höchster WAI-ARIA-Präzision, sauberem Fokus-Management und exzellenter Tastaturnavigation.",
    brandGradient: "from-indigo-950 via-violet-950 to-black",
  },
  {
    name: "Radix Vue",
    url: "https://www.radix-vue.com",
    image: "https://www.radix-vue.com/og.png",
    packageName: "radix-vue",
    badge: "Vue Headless",
    paradigm: "headless",
    categories: ["headless", "vue-svelte"],
    features: {
      hasLayout: false,
      hasSkeleton: false,
      a11yFocus: true,
      isTailwindNative: false,
    },
    frameworks: ["vue"],
    tags: ["Vue 3", "Headless", "shadcn-vue", "Accessible"],
    description:
      "Die beliebte Vue-3-Adaption von Radix UI – das robuste Barrierefreiheits-Fundament für moderne Vue-Designsysteme und shadcn-vue.",
    brandGradient: "from-emerald-950 via-slate-900 to-black",
  },
  {
    name: "PrimeReact",
    url: "https://primereact.org",
    image: "https://primefaces.org/cdn/primereact/images/primereact-logo.png",
    packageName: "primereact",
    badge: "React Enterprise",
    paradigm: "suite",
    categories: ["popular", "suite"],
    features: {
      hasLayout: true,
      hasSkeleton: true,
      a11yFocus: true,
      isTailwindNative: false,
    },
    frameworks: ["react"],
    tags: ["React", "80+ Components", "Data Tables", "Enterprise"],
    description:
      "Reichhaltige React-Komponentensuite mit über 80 vorgefertigten UI-Elementen von interaktiven Tabellen bis hin zu Gantt-Diagrammen.",
    brandGradient: "from-cyan-950 via-blue-950 to-neutral-950",
  },
  {
    name: "Solid UI",
    url: "https://solid-ui.com",
    image: "https://solid-ui.com/og.jpg",
    packageName: "@kobalte/core",
    badge: "SolidJS Copy-Paste",
    paradigm: "copy-paste",
    categories: ["tailwind", "vue-svelte", "copy-paste"],
    features: {
      hasLayout: false,
      hasSkeleton: true,
      a11yFocus: true,
      isTailwindNative: true,
    },
    frameworks: ["solid"],
    tags: ["SolidJS", "shadcn Port", "Tailwind CSS", "Kobalte"],
    description:
      "Der shadcn/ui-Port für das reaktive SolidJS-Ökosystem, basierend auf Kobalte-Primitives mit Copy-Paste-Workflow.",
    brandGradient: "from-blue-950 via-sky-950 to-black",
  },
  {
    name: "Kobalte",
    url: "https://kobalte.dev",
    image: "https://kobalte.dev/og.png",
    packageName: "@kobalte/core",
    badge: "SolidJS Headless",
    paradigm: "headless",
    categories: ["headless"],
    features: {
      hasLayout: false,
      hasSkeleton: false,
      a11yFocus: true,
      isTailwindNative: false,
    },
    frameworks: ["solid"],
    tags: ["SolidJS", "Headless", "WAI-ARIA", "Accessible"],
    description:
      "Ungestyltes, barrierefreies UI-Toolkit für SolidJS, das saubere Primitives für Dialoge, Dropdowns, Tooltips und Tabs bereitstellt.",
    brandGradient: "from-sky-950 via-indigo-950 to-black",
  },
  {
    name: "Flowbite",
    url: "https://flowbite.com",
    image: "https://flowbite.com/images/og-image.png",
    packageName: "flowbite",
    badge: "Tailwind Ecosystem",
    paradigm: "suite",
    categories: ["tailwind", "suite"],
    features: {
      hasLayout: true,
      hasSkeleton: true,
      a11yFocus: false,
      isTailwindNative: true,
    },
    frameworks: ["react", "vue", "agnostic"],
    tags: ["Tailwind CSS", "Figma", "React", "Interactivity"],
    description:
      "Komponenten-Ökosystem auf Basis von Tailwind CSS mit fertigen interaktiven Elementen, Figma-Kit und responsive Vorlagen.",
    brandGradient: "from-blue-950 via-slate-900 to-black",
  },
  {
    name: "Material Web",
    url: "https://material-web.dev",
    image: "https://material-web.dev/images/og-image.png",
    packageName: "@material/web",
    badge: "Google Material 3",
    paradigm: "web-components",
    categories: ["headless", "web-components"],
    features: {
      hasLayout: false,
      hasSkeleton: false,
      a11yFocus: true,
      isTailwindNative: false,
    },
    frameworks: ["agnostic"],
    tags: ["Web Components", "Google", "Material 3", "Framework-Agnostic"],
    description:
      "Googles offizielle Material 3 Web Components – direkt einsetzbar in React, Vue, Angular oder reinem Vanilla JavaScript.",
    brandGradient: "from-neutral-900 via-stone-900 to-black",
  },
  {
    name: "Zag.js",
    url: "https://zagjs.com",
    image: "https://zagjs.com/og-image.png",
    packageName: "@zag-js/react",
    badge: "Logic State Machines",
    paradigm: "headless",
    categories: ["headless"],
    features: {
      hasLayout: false,
      hasSkeleton: false,
      a11yFocus: true,
      isTailwindNative: false,
    },
    frameworks: ["react", "vue", "solid", "agnostic"],
    tags: ["State Machines", "Framework-Agnostic", "Logic Only", "XState"],
    description:
      "Framework-unabhängige UI-Komponenten-Zustandsmaschinen zur strikten Trennung von Interaktionslogik und Framework-Rendering.",
    brandGradient: "from-red-950 via-neutral-900 to-black",
  },
  {
    name: "gluestack-ui",
    url: "https://gluestack.io",
    image: "https://gluestack.io/images/og.png",
    packageName: "@gluestack-ui/themed",
    badge: "Universal Web & Native",
    paradigm: "suite",
    categories: ["popular", "tailwind", "suite"],
    features: {
      hasLayout: true,
      hasSkeleton: true,
      a11yFocus: true,
      isTailwindNative: true,
    },
    frameworks: ["react"],
    tags: ["React", "React Native", "Next.js", "Cross-Platform"],
    description:
      "Universelle UI-Komponenten für Web (Next.js) und Mobile (React Native) mit konsistenter Styling-Engine und hoher Performance.",
    brandGradient: "from-slate-900 via-neutral-950 to-black",
  },
  {
    name: "Preline UI",
    url: "https://preline.co",
    image: "https://preline.co/assets/img/og-image.png",
    packageName: "preline",
    badge: "Tailwind Library",
    paradigm: "suite",
    categories: ["tailwind", "suite"],
    features: {
      hasLayout: true,
      hasSkeleton: true,
      a11yFocus: false,
      isTailwindNative: true,
    },
    frameworks: ["agnostic", "react"],
    tags: ["Tailwind CSS", "Templates", "Prebuilt UI", "Vanilla JS"],
    description:
      "Große Sammlung vorfertigter Tailwind-Komponenten und Plugins für rasante Umsetzung von Landing-Pages und interaktiven Dashboards.",
    brandGradient: "from-blue-950 via-indigo-950 to-neutral-950",
  },
  {
    name: "Magic UI",
    url: "https://magicui.design",
    image: "https://magicui.design/og",
    packageName: "magicui",
    installCommand: "pnpm dlx shadcn@latest add https://magicui.design/r/marquee",
    badge: "Animated & Modern",
    paradigm: "visuals",
    categories: ["popular", "tailwind", "visuals"],
    features: {
      hasLayout: false,
      hasSkeleton: false,
      a11yFocus: false,
      isTailwindNative: true,
      hasAnimation: true,
    },
    frameworks: ["react"],
    tags: ["React", "Tailwind CSS", "Framer Motion", "Landing Pages"],
    description:
      "Wunderschön animierte React- und Tailwind-Komponenten für beeindruckende Landing-Pages, Hero-Sections und visuelle Highlights.",
    brandGradient: "from-purple-950 via-pink-950 to-black",
  },
  {
    name: "Aceternity UI",
    url: "https://ui.aceternity.com",
    image: "https://assets.aceternity.com/demos/aceternity-ui.png",
    packageName: "framer-motion clsx tailwind-merge",
    installCommand: "pnpm dlx shadcn@latest add ...",
    badge: "Animated & Visuals",
    paradigm: "visuals",
    categories: ["popular", "tailwind", "visuals"],
    features: {
      hasLayout: false,
      hasSkeleton: false,
      a11yFocus: false,
      isTailwindNative: true,
      hasAnimation: true,
    },
    frameworks: ["react"],
    tags: ["React", "Tailwind CSS", "Framer Motion", "Landing Pages", "Copy-Paste"],
    description:
      "Spektakuläre, animierte UI-Komponenten für Next.js und React. Bietet 3D-Karten, Spotlight-Effekte, Lampen-Glows, Marquees und dynamische Canvas-Shader.",
    brandGradient: "from-cyan-950 via-indigo-950 to-black",
  },
  {
    name: "React Aria (Adobe)",
    url: "https://react-spectrum.adobe.com/react-aria/",
    image: "https://react-spectrum.adobe.com/ReactAria_960x540_2x.png",
    packageName: "react-aria-components",
    installCommand: "pnpm add react-aria-components",
    badge: "Accessibility Goldstandard",
    paradigm: "headless",
    categories: ["popular", "headless"],
    features: {
      hasLayout: false,
      hasSkeleton: false,
      a11yFocus: true,
      isTailwindNative: false,
    },
    frameworks: ["react"],
    tags: ["Adobe", "React", "Headless", "WAI-ARIA", "Internationalization", "Touch"],
    description:
      "Adobes industrieweit anerkannte Bibliothek barrierefreier React-Komponenten mit Unterstützung für Screenreader, Tastaturnavigation und adaptive Gesten.",
    brandGradient: "from-red-950 via-rose-950 to-neutral-950",
  },
  {
    name: "Base UI",
    url: "https://base-ui.com",
    image: "https://base-ui.com/static/og-image.png",
    packageName: "@base-ui-components/react",
    badge: "MUI Headless Next-Gen",
    paradigm: "headless",
    categories: ["headless", "popular"],
    features: {
      hasLayout: false,
      hasSkeleton: false,
      a11yFocus: true,
      isTailwindNative: false,
    },
    frameworks: ["react"],
    tags: ["MUI Team", "Headless", "React", "Zero Runtime", "WAI-ARIA", "Unstyled"],
    description:
      "Das moderne Headless-Projekt des MUI-Teams: Unstyled, hochperformant und mit nativer Unterstützung für moderne CSS-Features wie Anchor-Positioning.",
    brandGradient: "from-sky-950 via-neutral-900 to-black",
  },
  {
    name: "Origin UI",
    url: "https://originui.com",
    image: "https://originui.com/og-image.png",
    packageName: "lucide-react",
    installCommand: "pnpm dlx shadcn@latest add ...",
    badge: "shadcn Micro-Components",
    paradigm: "copy-paste",
    categories: ["popular", "tailwind", "copy-paste"],
    features: {
      hasLayout: false,
      hasSkeleton: true,
      a11yFocus: true,
      isTailwindNative: true,
    },
    frameworks: ["react"],
    tags: ["shadcn/ui", "Tailwind CSS", "Inputs", "Searchbars", "Copy-Paste"],
    description:
      "Raffinierte Sammlung spezialisierter Micro-Komponenten für shadcn/ui: Detailverliebte Eingabefelder, Suchleisten, Filter-Menüs, Badges und Dialoge.",
    brandGradient: "from-violet-950 via-purple-950 to-black",
  },
  {
    name: "KoliBri (Public UI)",
    url: "https://public-ui.github.io/",
    image: "https://avatars.githubusercontent.com/u/104523910?s=400&v=4",
    packageName: "@public-ui/components",
    installCommand: "pnpm add @public-ui/components",
    badge: "BITV & Öffentlicher Dienst",
    paradigm: "web-components",
    categories: ["headless", "vue-svelte", "web-components"],
    features: {
      hasLayout: false,
      hasSkeleton: false,
      a11yFocus: true,
      isTailwindNative: false,
    },
    frameworks: ["agnostic"],
    tags: ["ITZBund", "Web Components", "BITV 2.0", "WCAG AAA", "GovTech"],
    description:
      "Die barrierefreie Open-Source-Komponentenbibliothek des ITZBund für den deutschen öffentlichen Sektor. Garantiert kompromisslose Barrierefreiheit nach BITV 2.0 / WCAG AAA und Framework-Unabhängigkeit via Web Components.",
    brandGradient: "from-blue-950 via-amber-950 to-slate-950",
  },
  {
    name: "Tamagui",
    url: "https://tamagui.dev",
    image: "https://tamagui.dev/social.png",
    packageName: "tamagui",
    badge: "Universal Web & Native",
    paradigm: "suite",
    categories: ["popular", "suite"],
    features: {
      hasLayout: true,
      hasSkeleton: true,
      a11yFocus: true,
      isTailwindNative: false,
      hasAnimation: true,
    },
    frameworks: ["react"],
    tags: ["Next.js", "React Native", "Compiler", "Cross-Platform", "Fast"],
    description:
      "Hochoptimiertes UI-System für universelle Apps (Next.js + React Native / Expo) mit spezialisiertem Optimizing-Compiler für minimale Bundle-Größen und native Performance.",
    brandGradient: "from-yellow-950 via-orange-950 to-black",
  },
];

function LibraryCardImage({
  src,
  alt,
  priority,
  badge,
  url,
  name,
  brandGradient,
}: {
  src: string;
  alt: string;
  priority: boolean;
  badge: string;
  url: string;
  name: string;
  brandGradient?: string;
}) {
  const [hasError, setHasError] = useState(false);

  return (
    <div className="relative aspect-[16/10] w-full overflow-hidden bg-neutral-100 dark:bg-neutral-800">
      {!hasError ? (
        <Image
          src={src}
          alt={alt}
          fill
          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, (max-width: 1536px) 33vw, 25vw"
          priority={priority}
          onError={() => setHasError(true)}
          className="object-cover transition-transform duration-500 ease-out group-hover:scale-105"
        />
      ) : (
        <div
          className={`flex h-full w-full items-center justify-center p-6 text-center transition-transform duration-500 ease-out group-hover:scale-105 bg-gradient-to-br ${
            brandGradient || "from-neutral-800 via-neutral-900 to-black"
          } text-white`}
        >
          <div className="flex flex-col items-center gap-1">
            <span className="font-mono text-[10px] uppercase tracking-widest text-white/60">
              Component Library
            </span>
            <span className="font-heading text-lg font-bold tracking-tight text-white">
              {name}
            </span>
          </div>
        </div>
      )}

      {/* Subtle Gradient Overlay */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-80" />

      {/* Top Stats & Links overlay */}
      <div className="pointer-events-auto absolute inset-x-2.5 top-2.5 flex items-center justify-between">
        <span className="rounded-full border border-white/20 bg-black/60 px-2 py-0.5 font-mono text-[10px] font-semibold text-white backdrop-blur-md">
          {badge}
        </span>

        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          title={`${name} Website öffnen`}
          aria-label={`${name} Website in neuem Tab öffnen`}
          className="flex h-6 w-6 items-center justify-center rounded-full border border-white/20 bg-black/60 text-white backdrop-blur-md transition-all hover:scale-110 hover:bg-black/80"
        >
          <ExternalLink className="h-3 w-3" />
        </a>
      </div>
    </div>
  );
}

export default function Detail() {
  const [search, setSearch] = useState("");
  const [selectedParadigm, setSelectedParadigm] = useState<string>("all");
  const [selectedFramework, setSelectedFramework] = useState<string>("all");
  const [criteriaLayout, setCriteriaLayout] = useState<boolean | null>(null);
  const [criteriaA11y, setCriteriaA11y] = useState(false);
  const [criteriaSkeleton, setCriteriaSkeleton] = useState(false);
  const [criteriaAnimation, setCriteriaAnimation] = useState(false);

  const [copiedPackage, setCopiedPackage] = useState<string | null>(null);
  const [density, setDensity] = useState<"compact" | "normal">("compact");
  const [showGuide, setShowGuide] = useState(false);

  const activeFiltersCount =
    (selectedParadigm !== "all" ? 1 : 0) +
    (selectedFramework !== "all" ? 1 : 0) +
    (criteriaLayout !== null ? 1 : 0) +
    (criteriaA11y ? 1 : 0) +
    (criteriaSkeleton ? 1 : 0) +
    (criteriaAnimation ? 1 : 0) +
    (search.trim() ? 1 : 0);

  const resetAllFilters = () => {
    setSelectedParadigm("all");
    setSelectedFramework("all");
    setCriteriaLayout(null);
    setCriteriaA11y(false);
    setCriteriaSkeleton(false);
    setCriteriaAnimation(false);
    setSearch("");
  };

  const filteredLibs = useMemo(() => {
    return COMPONENT_LIBS.filter((lib) => {
      // 1. Paradigma / Kategorie Filter
      if (selectedParadigm !== "all") {
        if (selectedParadigm === "popular") {
          if (!lib.categories.includes("popular")) return false;
        } else if (selectedParadigm === "tailwind") {
          if (!lib.categories.includes("tailwind") && !lib.features.isTailwindNative) return false;
        } else if (selectedParadigm === "copy-paste") {
          if (lib.paradigm !== "copy-paste") return false;
        } else if (selectedParadigm === "headless") {
          if (lib.paradigm !== "headless") return false;
        } else if (selectedParadigm === "suite") {
          if (lib.paradigm !== "suite") return false;
        } else if (selectedParadigm === "web-components") {
          if (lib.paradigm !== "web-components") return false;
        } else if (selectedParadigm === "visuals") {
          if (lib.paradigm !== "visuals") return false;
        }
      }

      // 2. Framework Filter
      if (selectedFramework !== "all") {
        if (
          !lib.frameworks.includes(selectedFramework as any) &&
          !lib.frameworks.includes("agnostic")
        ) {
          return false;
        }
      }

      // 3. Kriterien Toggles
      if (criteriaLayout !== null) {
        if (lib.features.hasLayout !== criteriaLayout) return false;
      }
      if (criteriaA11y && !lib.features.a11yFocus) return false;
      if (criteriaSkeleton && !lib.features.hasSkeleton) return false;
      if (criteriaAnimation && !lib.features.hasAnimation) return false;

      // 4. Text-Suche
      if (!search.trim()) return true;

      const q = search.toLowerCase();
      return (
        lib.name.toLowerCase().includes(q) ||
        lib.description.toLowerCase().includes(q) ||
        lib.packageName.toLowerCase().includes(q) ||
        lib.badge.toLowerCase().includes(q) ||
        lib.tags.some((t) => t.toLowerCase().includes(q))
      );
    });
  }, [
    search,
    selectedParadigm,
    selectedFramework,
    criteriaLayout,
    criteriaA11y,
    criteriaSkeleton,
    criteriaAnimation,
  ]);

  const copyCommand = (lib: ComponentLib) => {
    const cmd = lib.installCommand || `pnpm add ${lib.packageName}`;
    navigator.clipboard.writeText(cmd);
    setCopiedPackage(lib.name);
    setTimeout(() => {
      setCopiedPackage((current) => (current === lib.name ? null : current));
    }, 2000);
  };

  return (
    <div className="w-full space-y-8">
      {/* ─── Entscheidungshilfe & Architektur-Guide ─── */}
      <div className="overflow-hidden rounded-2xl border border-neutral-200/90 bg-neutral-50/70 shadow-xs dark:border-neutral-800/90 dark:bg-neutral-900/40">
        {/* Header Bar */}
        <div className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between border-b border-neutral-200/70 dark:border-neutral-800/70">
          <div className="flex items-start sm:items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-600/10 text-blue-600 dark:bg-blue-500/20 dark:text-blue-400">
              <Compass className="h-5 w-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-heading text-base font-bold text-neutral-900 dark:text-white">
                  Entscheidungshilfe: Welche UI-Bibliothek passt wann?
                </h3>
                <span className="hidden sm:inline-flex items-center rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300">
                  Praxis-Leitfaden
                </span>
              </div>
              <p className="text-xs text-neutral-600 dark:text-neutral-400 mt-0.5">
                Kriterien für Barrierefreiheit, Anpassbarkeit und Integrierbarkeit im Vergleich.
              </p>
            </div>
          </div>

          <button
            onClick={() => setShowGuide(!showGuide)}
            className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-neutral-200/90 bg-white px-3.5 py-1.5 text-xs font-medium text-neutral-700 shadow-2xs transition-all hover:bg-neutral-50 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-200 dark:hover:bg-neutral-700/80"
          >
            <span>{showGuide ? "Details schließen" : "Architektur-Details & Kriterien"}</span>
            {showGuide ? (
              <ChevronUp className="h-3.5 w-3.5 opacity-70" />
            ) : (
              <ChevronDown className="h-3.5 w-3.5 opacity-70" />
            )}
          </button>
        </div>

        {/* Quick Scenario Picker (always visible) */}
        <div className="p-4 sm:p-5 bg-white/50 dark:bg-neutral-950/30">
          <p className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-2.5">
            Schnellwahl nach Projekt-Szenario (aktiviert Filter):
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
            <button
              onClick={() => {
                resetAllFilters();
                setSelectedParadigm("copy-paste");
                setSelectedFramework("react");
              }}
              className="group flex flex-col text-left p-3 rounded-xl border border-neutral-200/80 bg-white dark:border-neutral-800 dark:bg-neutral-900 hover:border-blue-500/50 hover:shadow-xs transition-all"
            >
              <span className="text-xs font-bold text-neutral-900 dark:text-white flex items-center justify-between">
                <span>🚀 Modernes SaaS / Startup</span>
                <ArrowRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity text-blue-600 dark:text-blue-400" />
              </span>
              <span className="text-[11px] text-neutral-500 dark:text-neutral-400 mt-1">
                Filter: <strong className="text-neutral-800 dark:text-neutral-200">Copy-Paste</strong> (shadcn/ui, Magic UI)
              </span>
            </button>

            <button
              onClick={() => {
                resetAllFilters();
                setSelectedParadigm("suite");
                setCriteriaLayout(true);
              }}
              className="group flex flex-col text-left p-3 rounded-xl border border-neutral-200/80 bg-white dark:border-neutral-800 dark:bg-neutral-900 hover:border-blue-500/50 hover:shadow-xs transition-all"
            >
              <span className="text-xs font-bold text-neutral-900 dark:text-white flex items-center justify-between">
                <span>🏢 Enterprise & B2B Dashboard</span>
                <ArrowRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity text-blue-600 dark:text-blue-400" />
              </span>
              <span className="text-[11px] text-neutral-500 dark:text-neutral-400 mt-1">
                Filter: <strong className="text-neutral-800 dark:text-neutral-200">Enterprise Suiten</strong> mit Layout
              </span>
            </button>

            <button
              onClick={() => {
                resetAllFilters();
                setCriteriaA11y(true);
              }}
              className="group flex flex-col text-left p-3 rounded-xl border border-neutral-200/80 bg-white dark:border-neutral-800 dark:bg-neutral-900 hover:border-blue-500/50 hover:shadow-xs transition-all"
            >
              <span className="text-xs font-bold text-neutral-900 dark:text-white flex items-center justify-between">
                <span>🏛️ GovTech & Barrierefreiheit</span>
                <ArrowRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity text-blue-600 dark:text-blue-400" />
              </span>
              <span className="text-[11px] text-neutral-500 dark:text-neutral-400 mt-1">
                Filter: <strong className="text-neutral-800 dark:text-neutral-200">a11y-Fokus</strong> (BITV / WAI-ARIA)
              </span>
            </button>

            <button
              onClick={() => {
                resetAllFilters();
                setSelectedParadigm("headless");
              }}
              className="group flex flex-col text-left p-3 rounded-xl border border-neutral-200/80 bg-white dark:border-neutral-800 dark:bg-neutral-900 hover:border-blue-500/50 hover:shadow-xs transition-all"
            >
              <span className="text-xs font-bold text-neutral-900 dark:text-white flex items-center justify-between">
                <span>🎨 Eigenes Design-System</span>
                <ArrowRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity text-blue-600 dark:text-blue-400" />
              </span>
              <span className="text-[11px] text-neutral-500 dark:text-neutral-400 mt-1">
                Filter: <strong className="text-neutral-800 dark:text-neutral-200">Headless Primitives</strong> (0px CSS)
              </span>
            </button>
          </div>
        </div>

        {/* Collapsible Deep-Dive Guide */}
        {showGuide && (
          <div className="border-t border-neutral-200/80 dark:border-neutral-800/80 p-5 sm:p-6 space-y-6">
            {/* Paradigms Matrix */}
            <div>
              <h4 className="font-heading text-sm font-bold text-neutral-900 dark:text-white mb-3 flex items-center gap-1.5">
                <Layers className="h-4 w-4 text-neutral-500" />
                <span>Die 4 Architektur-Paradigmen im Vergleich</span>
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* 1. Copy-Paste */}
                <div className="rounded-xl border border-neutral-200/80 bg-white/80 p-4 dark:border-neutral-800 dark:bg-neutral-950/80 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center justify-between gap-1.5 mb-2">
                      <span className="text-xs font-bold text-neutral-900 dark:text-white">1. Copy-Paste / Ownership</span>
                      <span className="rounded bg-blue-500/10 px-1.5 py-0.5 text-[10px] font-medium text-blue-600 dark:text-blue-400">shadcn</span>
                    </div>
                    <p className="text-xs text-neutral-600 dark:text-neutral-400 leading-relaxed">
                      Der UI-Code liegt direkt in deiner Codebase. Volle Kontrolle über JSX und Tailwind ohne NPM-Blackbox.
                    </p>
                    <div className="mt-3 space-y-1 text-[11px]">
                      <div className="flex items-center justify-between text-neutral-600 dark:text-neutral-400">
                        <span>Anpassbarkeit:</span>
                        <span className="font-semibold text-emerald-600 dark:text-emerald-400">Maximal (100%)</span>
                      </div>
                      <div className="flex items-center justify-between text-neutral-600 dark:text-neutral-400">
                        <span>Barrierefreiheit:</span>
                        <span className="font-semibold text-emerald-600 dark:text-emerald-400">Sehr hoch (Radix)</span>
                      </div>
                      <div className="flex items-center justify-between text-neutral-600 dark:text-neutral-400">
                        <span>Integrierbarkeit:</span>
                        <span className="font-semibold text-emerald-600 dark:text-emerald-400">Direkt via Tailwind</span>
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 pt-2.5 border-t border-neutral-100 dark:border-neutral-800/80">
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-rose-500 block mb-0.5">Wann meiden:</span>
                    <p className="text-[11px] text-neutral-500 dark:text-neutral-400">
                      Wenn man automatische `npm update`-Upgrades für den gesamten UI-Code erwartet.
                    </p>
                  </div>
                </div>

                {/* 2. Headless */}
                <div className="rounded-xl border border-neutral-200/80 bg-white/80 p-4 dark:border-neutral-800 dark:bg-neutral-950/80 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center justify-between gap-1.5 mb-2">
                      <span className="text-xs font-bold text-neutral-900 dark:text-white">2. Headless & Logic</span>
                      <span className="rounded bg-purple-500/10 px-1.5 py-0.5 text-[10px] font-medium text-purple-600 dark:text-purple-400">Radix / Aria</span>
                    </div>
                    <p className="text-xs text-neutral-600 dark:text-neutral-400 leading-relaxed">
                      Reine Interaktionslogik, Tastaturnavigation und WAI-ARIA-Attribute ohne jegliche Standard-Styles (0px CSS).
                    </p>
                    <div className="mt-3 space-y-1 text-[11px]">
                      <div className="flex items-center justify-between text-neutral-600 dark:text-neutral-400">
                        <span>Anpassbarkeit:</span>
                        <span className="font-semibold text-emerald-600 dark:text-emerald-400">Grenzenlos</span>
                      </div>
                      <div className="flex items-center justify-between text-neutral-600 dark:text-neutral-400">
                        <span>Barrierefreiheit:</span>
                        <span className="font-semibold text-emerald-600 dark:text-emerald-400">Goldstandard</span>
                      </div>
                      <div className="flex items-center justify-between text-neutral-600 dark:text-neutral-400">
                        <span>Setup-Aufwand:</span>
                        <span className="font-semibold text-amber-600 dark:text-amber-400">Höher (eigenes CSS)</span>
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 pt-2.5 border-t border-neutral-100 dark:border-neutral-800/80">
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-rose-500 block mb-0.5">Wann meiden:</span>
                    <p className="text-[11px] text-neutral-500 dark:text-neutral-400">
                      Wenn man morgen ein fertiges Interface fertigstellen muss und kein Design-Team hat.
                    </p>
                  </div>
                </div>

                {/* 3. All-in-One Suites */}
                <div className="rounded-xl border border-neutral-200/80 bg-white/80 p-4 dark:border-neutral-800 dark:bg-neutral-950/80 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center justify-between gap-1.5 mb-2">
                      <span className="text-xs font-bold text-neutral-900 dark:text-white">3. Enterprise Suiten</span>
                      <span className="rounded bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-medium text-amber-600 dark:text-amber-400">Mantine / MUI</span>
                    </div>
                    <p className="text-xs text-neutral-600 dark:text-neutral-400 leading-relaxed">
                      Große Komplettpakete mit über 100 fertigen Widgets, Daten-Tabellen, Date-Pickern und Form-Validierung.
                    </p>
                    <div className="mt-3 space-y-1 text-[11px]">
                      <div className="flex items-center justify-between text-neutral-600 dark:text-neutral-400">
                        <span>Feature-Umfang:</span>
                        <span className="font-semibold text-emerald-600 dark:text-emerald-400">Riesig (Out-of-the-Box)</span>
                      </div>
                      <div className="flex items-center justify-between text-neutral-600 dark:text-neutral-400">
                        <span>Time-to-Market:</span>
                        <span className="font-semibold text-emerald-600 dark:text-emerald-400">Sehr schnell</span>
                      </div>
                      <div className="flex items-center justify-between text-neutral-600 dark:text-neutral-400">
                        <span>Figma-Flexibilität:</span>
                        <span className="font-semibold text-amber-600 dark:text-amber-400">Mittel ("Fighting Theme")</span>
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 pt-2.5 border-t border-neutral-100 dark:border-neutral-800/80">
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-rose-500 block mb-0.5">Wann meiden:</span>
                    <p className="text-[11px] text-neutral-500 dark:text-neutral-400">
                      Bei strikt vorgegebenem, speziellem Custom-Brand-Design oder B2C-Landingpages.
                    </p>
                  </div>
                </div>

                {/* 4. Public Sector & Web Components */}
                <div className="rounded-xl border border-neutral-200/80 bg-white/80 p-4 dark:border-neutral-800 dark:bg-neutral-950/80 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center justify-between gap-1.5 mb-2">
                      <span className="text-xs font-bold text-neutral-900 dark:text-white">4. Web Components</span>
                      <span className="rounded bg-emerald-500/10 px-1.5 py-0.5 text-[10px] font-medium text-emerald-600 dark:text-emerald-400">Web Standards</span>
                    </div>
                    <p className="text-xs text-neutral-600 dark:text-neutral-400 leading-relaxed">
                      Auf W3C-Standards basierende Web-Components, die framework-agnostisch in React, Vue, Angular oder HTML laufen.
                    </p>
                    <div className="mt-3 space-y-1 text-[11px]">
                      <div className="flex items-center justify-between text-neutral-600 dark:text-neutral-400">
                        <span>Barrierefreiheit:</span>
                        <span className="font-semibold text-emerald-600 dark:text-emerald-400">BITV 2.0 / WCAG AAA</span>
                      </div>
                      <div className="flex items-center justify-between text-neutral-600 dark:text-neutral-400">
                        <span>Framework-Unabhängig:</span>
                        <span className="font-semibold text-emerald-600 dark:text-emerald-400">100% zukunftssicher</span>
                      </div>
                      <div className="flex items-center justify-between text-neutral-600 dark:text-neutral-400">
                        <span>Integrierbarkeit:</span>
                        <span className="font-semibold text-neutral-700 dark:text-neutral-300">Überall einsetzbar</span>
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 pt-2.5 border-t border-neutral-100 dark:border-neutral-800/80">
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-rose-500 block mb-0.5">Wann meiden:</span>
                    <p className="text-[11px] text-neutral-500 dark:text-neutral-400">
                      Wenn man tief mit React-Server-Components und Tailwind-Utilities verwebt entwickeln will.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* 3 Core Architecture Questions */}
            <div className="rounded-xl border border-blue-200/70 bg-blue-50/50 p-4 dark:border-blue-950 dark:bg-blue-950/20">
              <h5 className="text-xs font-bold text-blue-900 dark:text-blue-200 mb-2 flex items-center gap-1.5">
                <Lightbulb className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                <span>3 Leitfragen vor der Bibliotheks-Entscheidung:</span>
              </h5>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs text-blue-950/80 dark:text-blue-200/80">
                <div className="bg-white/60 dark:bg-neutral-900/60 p-3 rounded-lg border border-blue-100 dark:border-blue-900/50">
                  <strong className="block font-semibold text-neutral-900 dark:text-white mb-1">1. Wie strikt ist das Design?</strong>
                  Muss ein 1:1 Figma-Design umgesetzt werden? Dann meide All-in-One-Pakete und wähle <em>shadcn/ui</em> oder <em>Radix/Base UI</em>.
                </div>
                <div className="bg-white/60 dark:bg-neutral-900/60 p-3 rounded-lg border border-blue-100 dark:border-blue-900/50">
                  <strong className="block font-semibold text-neutral-900 dark:text-white mb-1">2. Gibt es gesetzliche Vorgaben?</strong>
                  Öffentlicher Sektor oder BFSG (Barrierefreiheitsstärkungsgesetz)? Greife zu <em>KoliBri</em> oder <em>React Aria</em>.
                </div>
                <div className="bg-white/60 dark:bg-neutral-900/60 p-3 rounded-lg border border-blue-100 dark:border-blue-900/50">
                  <strong className="block font-semibold text-neutral-900 dark:text-white mb-1">3. Wie sieht die Wartung aus?</strong>
                  Willst du Bugfixes automatisch über `pnpm update` oder maximale Code-Souveränität ohne Breaking Changes?
                </div>
              </div>
            </div>

            {/* ─── Die 6 Komponenten-Kategorien (Was steckt generell drin?) ─── */}
            <div>
              <div className="mb-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                <h4 className="font-heading text-sm font-bold text-neutral-900 dark:text-white flex items-center gap-1.5">
                  <LayoutTemplate className="h-4 w-4 text-neutral-500" />
                  <span>Die 6 Baustein-Kategorien (Was bieten moderne UI-Libs?)</span>
                </h4>
                <span className="text-[11px] text-neutral-500 dark:text-neutral-400">
                  Formulare sind oft nur ~25% des Gesamtumfangs
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5">
                {/* 1. Formulare */}
                <div className="rounded-xl border border-neutral-200/80 bg-white/70 p-3.5 dark:border-neutral-800 dark:bg-neutral-950/70">
                  <div className="flex items-center gap-2 mb-1.5">
                    <div className="flex h-6 w-6 items-center justify-center rounded-md bg-blue-500/10 text-blue-600 dark:bg-blue-500/20 dark:text-blue-400">
                      <CheckSquare className="h-3.5 w-3.5" />
                    </div>
                    <span className="text-xs font-bold text-neutral-900 dark:text-white">1. Eingabe & Formulare</span>
                  </div>
                  <p className="text-[11px] text-neutral-600 dark:text-neutral-400 leading-relaxed mb-2">
                    Buttons, Inputs, Combobox (Suchfeld-Dropdown), Date-Range-Picker, Checkboxen, Slider und OTP-Pin-Felder.
                  </p>
                  <span className="inline-block rounded bg-neutral-100 dark:bg-neutral-800 px-1.5 py-0.5 text-[10px] font-mono text-neutral-600 dark:text-neutral-400">
                    Standard bei fast allen Libs
                  </span>
                </div>

                {/* 2. Feedback & Skeletons */}
                <div className="rounded-xl border border-neutral-200/80 bg-white/70 p-3.5 dark:border-neutral-800 dark:bg-neutral-950/70">
                  <div className="flex items-center gap-2 mb-1.5">
                    <div className="flex h-6 w-6 items-center justify-center rounded-md bg-amber-500/10 text-amber-600 dark:bg-amber-500/20 dark:text-amber-400">
                      <Activity className="h-3.5 w-3.5" />
                    </div>
                    <span className="text-xs font-bold text-neutral-900 dark:text-white">2. Feedback & Ladezustände</span>
                  </div>
                  <p className="text-[11px] text-neutral-600 dark:text-neutral-400 leading-relaxed mb-2">
                    <strong>Skeleton-Loader</strong> (heute Goldstandard gegen Layout Shifts / CLS!), Toasts (Sonner), Alert-Banner und Progress-Bars.
                  </p>
                  <span className="inline-block rounded bg-amber-500/10 text-amber-700 dark:text-amber-300 px-1.5 py-0.5 text-[10px] font-medium">
                    Skeletons: essenziell für Suspense/SSR
                  </span>
                </div>

                {/* 3. Overlays & Floating */}
                <div className="rounded-xl border border-neutral-200/80 bg-white/70 p-3.5 dark:border-neutral-800 dark:bg-neutral-950/70">
                  <div className="flex items-center gap-2 mb-1.5">
                    <div className="flex h-6 w-6 items-center justify-center rounded-md bg-purple-500/10 text-purple-600 dark:bg-purple-500/20 dark:text-purple-400">
                      <AppWindow className="h-3.5 w-3.5" />
                    </div>
                    <span className="text-xs font-bold text-neutral-900 dark:text-white">3. Overlays & Dialoge</span>
                  </div>
                  <p className="text-[11px] text-neutral-600 dark:text-neutral-400 leading-relaxed mb-2">
                    Modale Dialoge mit Fokus-Falle (Focus-Trap), Sheets/Drawers (mobile Bottom-Sheets), Popover und Command Menus (⌘K).
                  </p>
                  <span className="inline-block rounded bg-purple-500/10 text-purple-700 dark:text-purple-300 px-1.5 py-0.5 text-[10px] font-medium">
                    a11y-Schwerpunkt (Radix, Aria)
                  </span>
                </div>

                {/* 4. Navigation */}
                <div className="rounded-xl border border-neutral-200/80 bg-white/70 p-3.5 dark:border-neutral-800 dark:bg-neutral-950/70">
                  <div className="flex items-center gap-2 mb-1.5">
                    <div className="flex h-6 w-6 items-center justify-center rounded-md bg-emerald-500/10 text-emerald-600 dark:bg-emerald-500/20 dark:text-emerald-400">
                      <Compass className="h-3.5 w-3.5" />
                    </div>
                    <span className="text-xs font-bold text-neutral-900 dark:text-white">4. Navigation & Wayfinding</span>
                  </div>
                  <p className="text-[11px] text-neutral-600 dark:text-neutral-400 leading-relaxed mb-2">
                    App-Sidebars (mit Collapse & Mobile-Drawer), Tabs, Mega-Flyout-Menüs, Breadcrumbs und Pagination.
                  </p>
                  <span className="inline-block rounded bg-neutral-100 dark:bg-neutral-800 px-1.5 py-0.5 text-[10px] font-mono text-neutral-600 dark:text-neutral-400">
                    Strukturierte Informationsarchitektur
                  </span>
                </div>

                {/* 5. Daten-Präsentation */}
                <div className="rounded-xl border border-neutral-200/80 bg-white/70 p-3.5 dark:border-neutral-800 dark:bg-neutral-950/70">
                  <div className="flex items-center gap-2 mb-1.5">
                    <div className="flex h-6 w-6 items-center justify-center rounded-md bg-rose-500/10 text-rose-600 dark:bg-rose-500/20 dark:text-rose-400">
                      <Table className="h-3.5 w-3.5" />
                    </div>
                    <span className="text-xs font-bold text-neutral-900 dark:text-white">5. Daten-Präsentation</span>
                  </div>
                  <p className="text-[11px] text-neutral-600 dark:text-neutral-400 leading-relaxed mb-2">
                    Interaktive DataTables (TanStack Table mit Sortierung & Spaltenfilter), Avatare mit Initialen-Fallback, Kbd-Tastaturkürzel.
                  </p>
                  <span className="inline-block rounded bg-rose-500/10 text-rose-700 dark:text-rose-300 px-1.5 py-0.5 text-[10px] font-medium">
                    Kernelement in B2B & Dashboards
                  </span>
                </div>

                {/* 6. Layout & Struktur */}
                <div className="rounded-xl border border-neutral-200/80 bg-white/70 p-3.5 dark:border-neutral-800 dark:bg-neutral-950/70">
                  <div className="flex items-center gap-2 mb-1.5">
                    <div className="flex h-6 w-6 items-center justify-center rounded-md bg-cyan-500/10 text-cyan-600 dark:bg-cyan-500/20 dark:text-cyan-400">
                      <Columns className="h-3.5 w-3.5" />
                    </div>
                    <span className="text-xs font-bold text-neutral-900 dark:text-white">6. Layout & Struktur</span>
                  </div>
                  <p className="text-[11px] text-neutral-600 dark:text-neutral-400 leading-relaxed mb-2">
                    Suiten bieten Stack, Grid, Container. Tailwind-Libs bieten Spezial-Layouts wie Resizable Panels (Splits) und ScrollArea.
                  </p>
                  <span className="inline-block rounded bg-cyan-500/10 text-cyan-700 dark:text-cyan-300 px-1.5 py-0.5 text-[10px] font-medium">
                    Headless & Web Components oft ohne Layout
                  </span>
                </div>
              </div>
            </div>

            {/* ─── Wer bietet was? Feature-Scope Matrix ─── */}
            <div className="overflow-x-auto rounded-xl border border-neutral-200/80 bg-white/90 p-4 dark:border-neutral-800 dark:bg-neutral-950/90">
              <h4 className="font-heading text-sm font-bold text-neutral-900 dark:text-white mb-1.5 flex items-center gap-1.5">
                <Columns className="h-4 w-4 text-neutral-500" />
                <span>Feature-Scope: Welche Bibliotheks-Familie bietet was?</span>
              </h4>
              <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-3.5">
                Vergleich der bereitgestellten Bausteine nach Architektur-Typ:
              </p>

              <div className="min-w-[620px] divide-y divide-neutral-100 dark:divide-neutral-800 text-xs">
                {/* Header row */}
                <div className="grid grid-cols-6 pb-2 font-semibold text-neutral-500 dark:text-neutral-400 text-[11px]">
                  <span className="col-span-2">Bibliotheks-Typ & Beispiele</span>
                  <span>Formulare</span>
                  <span>Skeletons</span>
                  <span>Overlays (Dialoge)</span>
                  <span>Layout-System</span>
                </div>

                {/* Row 1: All-in-One Suites */}
                <div className="grid grid-cols-6 py-2.5 items-center">
                  <div className="col-span-2 pr-2">
                    <strong className="block text-neutral-900 dark:text-white">Enterprise Suiten</strong>
                    <span className="text-[10px] text-neutral-500">Mantine, MUI, PrimeReact</span>
                  </div>
                  <span className="text-emerald-600 dark:text-emerald-400 font-medium">✔ Vollständig</span>
                  <span className="text-emerald-600 dark:text-emerald-400 font-medium">✔ Integriert</span>
                  <span className="text-emerald-600 dark:text-emerald-400 font-medium">✔ Fertig gestylt</span>
                  <span className="text-emerald-600 dark:text-emerald-400 font-medium">✔ Stack, Grid, Box</span>
                </div>

                {/* Row 2: Copy-Paste */}
                <div className="grid grid-cols-6 py-2.5 items-center">
                  <div className="col-span-2 pr-2">
                    <strong className="block text-neutral-900 dark:text-white">Copy-Paste / Ownership</strong>
                    <span className="text-[10px] text-neutral-500">shadcn/ui, Origin UI</span>
                  </div>
                  <span className="text-emerald-600 dark:text-emerald-400 font-medium">✔ Vollständig</span>
                  <span className="text-emerald-600 dark:text-emerald-400 font-medium">✔ Skeleton-Preset</span>
                  <span className="text-emerald-600 dark:text-emerald-400 font-medium">✔ Radix-Basis</span>
                  <span className="text-blue-600 dark:text-blue-400 font-medium">Tailwind + Sidebar/Panels</span>
                </div>

                {/* Row 3: Headless */}
                <div className="grid grid-cols-6 py-2.5 items-center">
                  <div className="col-span-2 pr-2">
                    <strong className="block text-neutral-900 dark:text-white">Headless & Primitives</strong>
                    <span className="text-[10px] text-neutral-500">Radix UI, React Aria, Base UI</span>
                  </div>
                  <span className="text-emerald-600 dark:text-emerald-400 font-medium">✔ Nur Logik/ARIA</span>
                  <span className="text-neutral-400 font-medium">❌ (Nur CSS nötig)</span>
                  <span className="text-emerald-600 dark:text-emerald-400 font-medium">✔ Goldstandard</span>
                  <span className="text-rose-500 font-medium">❌ 0px Layout (Eigenes CSS)</span>
                </div>

                {/* Row 4: Web Components / Public Sector */}
                <div className="grid grid-cols-6 py-2.5 items-center">
                  <div className="col-span-2 pr-2">
                    <strong className="block text-neutral-900 dark:text-white">Web Components / GovTech</strong>
                    <span className="text-[10px] text-neutral-500">KoliBri, Shoelace</span>
                  </div>
                  <span className="text-emerald-600 dark:text-emerald-400 font-medium">✔ BITV / Standards</span>
                  <span className="text-neutral-500 font-medium">Minimal</span>
                  <span className="text-emerald-600 dark:text-emerald-400 font-medium">✔ Dialoge & Modals</span>
                  <span className="text-neutral-500 font-medium">❌ (Layout im Seiten-CSS)</span>
                </div>

                {/* Row 5: Visuals */}
                <div className="grid grid-cols-6 py-2.5 items-center">
                  <div className="col-span-2 pr-2">
                    <strong className="block text-neutral-900 dark:text-white">Visuelle Spezialisten</strong>
                    <span className="text-[10px] text-neutral-500">Magic UI, Aceternity, Tremor</span>
                  </div>
                  <span className="text-neutral-400 font-medium">❌ Kaum Formulare</span>
                  <span className="text-neutral-400 font-medium">❌</span>
                  <span className="text-neutral-400 font-medium">Teilweise</span>
                  <span className="text-neutral-400 font-medium">Reine Hero/Visuals</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ─── Filter, Search & View Cockpit ─── */}
      <div className="space-y-3 rounded-2xl border border-neutral-200/80 bg-neutral-50/60 p-3.5 sm:p-4 dark:border-neutral-800/80 dark:bg-neutral-900/40">
        {/* Row 1: Search bar, active filter reset & density toggle */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative w-full sm:max-w-md">
            <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Suchen (z. B. shadcn, tailwind, a11y, layout)..."
              className="w-full rounded-xl border border-neutral-200 bg-white py-2 pl-10 pr-3.5 text-xs sm:text-sm text-neutral-900 shadow-2xs transition-all placeholder:text-neutral-400 focus:border-neutral-500 focus:outline-none focus:ring-2 focus:ring-neutral-500/20 dark:border-neutral-800 dark:bg-neutral-950 dark:text-white dark:placeholder:text-neutral-500 dark:focus:border-neutral-400"
            />
          </div>

          <div className="flex items-center justify-between sm:justify-end gap-2.5">
            {activeFiltersCount > 0 && (
              <button
                onClick={resetAllFilters}
                className="inline-flex items-center gap-1 rounded-full border border-neutral-200 bg-white px-2.5 py-1 text-xs font-medium text-neutral-600 hover:text-neutral-900 hover:border-neutral-300 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-300 dark:hover:text-white transition-all"
              >
                <X className="h-3 w-3" />
                <span>Filter zurücksetzen ({filteredLibs.length})</span>
              </button>
            )}

            {/* Density toggle buttons */}
            <div className="hidden sm:flex items-center gap-1 rounded-full border border-neutral-200 bg-white p-1 dark:border-neutral-800 dark:bg-neutral-950">
              <button
                onClick={() => setDensity("compact")}
                title="Kompakte Ansicht (4 Spalten)"
                className={`rounded-full p-1.5 transition-all ${
                  density === "compact"
                    ? "bg-neutral-900 text-white dark:bg-white dark:text-neutral-900"
                    : "text-neutral-500 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-white"
                }`}
              >
                <Grid3X3 className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => setDensity("normal")}
                title="Standard Ansicht (3 Spalten)"
                className={`rounded-full p-1.5 transition-all ${
                  density === "normal"
                    ? "bg-neutral-900 text-white dark:bg-white dark:text-neutral-900"
                    : "text-neutral-500 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-white"
                }`}
              >
                <LayoutGrid className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        </div>

        {/* Row 2: Paradigma & Haupt-Tabs */}
        <div className="flex flex-wrap items-center gap-1.5 pt-1">
          {[
            { id: "all", label: "Alle", count: COMPONENT_LIBS.length },
            {
              id: "copy-paste",
              label: "Copy-Paste",
              count: COMPONENT_LIBS.filter((l) => l.paradigm === "copy-paste").length,
            },
            {
              id: "headless",
              label: "Headless & Primitives",
              count: COMPONENT_LIBS.filter((l) => l.paradigm === "headless").length,
            },
            {
              id: "suite",
              label: "Enterprise Suiten",
              count: COMPONENT_LIBS.filter((l) => l.paradigm === "suite").length,
            },
            {
              id: "tailwind",
              label: "Tailwind CSS",
              count: COMPONENT_LIBS.filter((l) => l.features.isTailwindNative || l.categories.includes("tailwind")).length,
            },
            {
              id: "web-components",
              label: "Web Components",
              count: COMPONENT_LIBS.filter((l) => l.paradigm === "web-components").length,
            },
            {
              id: "popular",
              label: "Populär",
              count: COMPONENT_LIBS.filter((l) => l.categories.includes("popular")).length,
            },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setSelectedParadigm(tab.id)}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-all ${
                selectedParadigm === tab.id
                  ? "bg-neutral-900 text-white shadow-xs dark:bg-white dark:text-neutral-900"
                  : "border border-neutral-200/80 bg-white/80 text-neutral-600 hover:border-neutral-300 hover:bg-white dark:border-neutral-800 dark:bg-neutral-950/80 dark:text-neutral-400 dark:hover:border-neutral-700 dark:hover:text-white"
              }`}
            >
              {tab.label}
              <span className="ml-1.5 opacity-60 font-mono text-[10px]">
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        {/* Row 3: Faceted Kriterien-Filter & Framework-Selector */}
        <div className="flex flex-wrap items-center justify-between gap-2.5 pt-2 border-t border-neutral-200/60 dark:border-neutral-800/60">
          {/* Feature criteria chips */}
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[11px] font-semibold text-neutral-400 dark:text-neutral-500 uppercase tracking-wider mr-1">
              Kriterien:
            </span>

            {/* Layout Toggle */}
            <button
              onClick={() =>
                setCriteriaLayout((prev) => (prev === true ? null : true))
              }
              className={`inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-[11px] font-medium transition-all border ${
                criteriaLayout === true
                  ? "border-cyan-500/50 bg-cyan-50 text-cyan-800 dark:bg-cyan-950/40 dark:text-cyan-300"
                  : "border-neutral-200/70 bg-white/60 text-neutral-600 hover:bg-white dark:border-neutral-800 dark:bg-neutral-950/60 dark:text-neutral-400"
              }`}
            >
              <Columns className="h-3 w-3" />
              <span>Mit Layout-System</span>
              {criteriaLayout === true && <Check className="h-3 w-3 ml-0.5 text-cyan-600" />}
            </button>

            {/* a11y Focus Toggle */}
            <button
              onClick={() => setCriteriaA11y(!criteriaA11y)}
              className={`inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-[11px] font-medium transition-all border ${
                criteriaA11y
                  ? "border-emerald-500/50 bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300"
                  : "border-neutral-200/70 bg-white/60 text-neutral-600 hover:bg-white dark:border-neutral-800 dark:bg-neutral-950/60 dark:text-neutral-400"
              }`}
            >
              <ShieldCheck className="h-3 w-3" />
              <span>a11y-Fokus / BITV</span>
              {criteriaA11y && <Check className="h-3 w-3 ml-0.5 text-emerald-600" />}
            </button>

            {/* Skeleton Toggle */}
            <button
              onClick={() => setCriteriaSkeleton(!criteriaSkeleton)}
              className={`inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-[11px] font-medium transition-all border ${
                criteriaSkeleton
                  ? "border-amber-500/50 bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300"
                  : "border-neutral-200/70 bg-white/60 text-neutral-600 hover:bg-white dark:border-neutral-800 dark:bg-neutral-950/60 dark:text-neutral-400"
              }`}
            >
              <Activity className="h-3 w-3" />
              <span>Mit Skeletons</span>
              {criteriaSkeleton && <Check className="h-3 w-3 ml-0.5 text-amber-600" />}
            </button>

            {/* Animation Toggle */}
            <button
              onClick={() => setCriteriaAnimation(!criteriaAnimation)}
              className={`inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-[11px] font-medium transition-all border ${
                criteriaAnimation
                  ? "border-purple-500/50 bg-purple-50 text-purple-800 dark:bg-purple-950/40 dark:text-purple-300"
                  : "border-neutral-200/70 bg-white/60 text-neutral-600 hover:bg-white dark:border-neutral-800 dark:bg-neutral-950/60 dark:text-neutral-400"
              }`}
            >
              <Sparkles className="h-3 w-3" />
              <span>Animationen & Visuals</span>
              {criteriaAnimation && <Check className="h-3 w-3 ml-0.5 text-purple-600" />}
            </button>
          </div>

          {/* Framework selector pills */}
          <div className="flex items-center gap-1">
            <span className="text-[11px] font-semibold text-neutral-400 dark:text-neutral-500 uppercase tracking-wider mr-1">
              Framework:
            </span>
            {[
              { id: "all", label: "Alle" },
              { id: "react", label: "React" },
              { id: "vue", label: "Vue" },
              { id: "svelte", label: "Svelte" },
              { id: "solid", label: "Solid" },
              { id: "agnostic", label: "Agnostisch" },
            ].map((fw) => (
              <button
                key={fw.id}
                onClick={() => setSelectedFramework(fw.id)}
                className={`rounded-md px-2 py-0.5 text-[11px] font-medium transition-all ${
                  selectedFramework === fw.id
                    ? "bg-neutral-800 text-white dark:bg-white dark:text-neutral-900"
                    : "text-neutral-500 hover:text-neutral-900 hover:bg-neutral-200/60 dark:text-neutral-400 dark:hover:text-white dark:hover:bg-neutral-800"
                }`}
              >
                {fw.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ─── Grid of Component Libraries ─── */}
      {filteredLibs.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-neutral-300 py-16 text-center dark:border-neutral-800">
          <Layers className="h-8 w-8 text-neutral-400 mb-2 opacity-60" />
          <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
            Keine Komponenten-Bibliotheken für die gewählten Filter gefunden.
          </p>
          <button
            onClick={resetAllFilters}
            className="mt-2 text-xs font-medium text-blue-600 underline hover:text-blue-500 dark:text-blue-400"
          >
            Alle Filter zurücksetzen
          </button>
        </div>
      ) : (
        <div
          className={`grid gap-5 ${
            density === "compact"
              ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
              : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
          }`}
        >
          {filteredLibs.map((lib, idx) => {
            const isCopied = copiedPackage === lib.name;
            const displayCmd =
              lib.installCommand || `pnpm add ${lib.packageName}`;

            return (
              <div
                key={lib.name}
                className="group relative flex flex-col overflow-hidden rounded-xl border border-black/10 bg-white/90 shadow-xs backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:border-black/25 hover:shadow-lg dark:border-white/10 dark:bg-neutral-900/80 dark:hover:border-white/25"
              >
                {/* ─── Image Container with Aspect Ratio ─── */}
                <LibraryCardImage
                  src={lib.image}
                  alt={`${lib.name} Vorschau`}
                  priority={idx < 4}
                  badge={lib.badge}
                  url={lib.url}
                  name={lib.name}
                  brandGradient={lib.brandGradient}
                />

                {/* ─── Card Content ─── */}
                <div className="flex flex-1 flex-col justify-between p-3.5">
                  <div>
                    {/* Header: Title */}
                    <div className="flex items-start justify-between gap-1.5">
                      <a
                        href={lib.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-heading inline-flex items-center gap-1 text-sm font-bold tracking-tight text-neutral-900 transition-colors hover:text-blue-600 dark:text-white dark:hover:text-blue-400"
                      >
                        {lib.name}
                      </a>
                    </div>

                    {/* Tags & Feature Badges */}
                    <div className="mt-1.5 flex flex-wrap items-center gap-1">
                      {/* Criteria Highlights */}
                      {lib.features.hasLayout && (
                        <span
                          title="Integriertes Layout-System"
                          className="rounded border border-cyan-500/30 bg-cyan-500/10 px-1.5 py-0.2 text-[9px] font-medium text-cyan-700 dark:text-cyan-300"
                        >
                          Layout
                        </span>
                      )}
                      {lib.features.a11yFocus && (
                        <span
                          title="Barrierefreiheits-Fokus (BITV / WAI-ARIA)"
                          className="rounded border border-emerald-500/30 bg-emerald-500/10 px-1.5 py-0.2 text-[9px] font-medium text-emerald-700 dark:text-emerald-300"
                        >
                          a11y
                        </span>
                      )}
                      {lib.features.hasSkeleton && (
                        <span
                          title="Integrierte Skeleton-Ladeplatzhalter"
                          className="rounded border border-amber-500/30 bg-amber-500/10 px-1.5 py-0.2 text-[9px] font-medium text-amber-700 dark:text-amber-300"
                        >
                          Skeleton
                        </span>
                      )}
                      {lib.features.hasAnimation && (
                        <span
                          title="Animationen & Visuals"
                          className="rounded border border-purple-500/30 bg-purple-500/10 px-1.5 py-0.2 text-[9px] font-medium text-purple-700 dark:text-purple-300"
                        >
                          Animiert
                        </span>
                      )}

                      {/* General Tags */}
                      {lib.tags.slice(0, 3).map((tag) => (
                        <span
                          key={tag}
                          className="rounded border border-neutral-200/70 bg-neutral-100/70 px-1.5 py-0.2 text-[10px] font-medium text-neutral-600 dark:border-neutral-800 dark:bg-neutral-800/70 dark:text-neutral-400"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>

                    {/* Description - clamped to 2 lines for compact height */}
                    <p className="mt-2 text-xs leading-relaxed text-neutral-600 dark:text-neutral-300 line-clamp-2">
                      {lib.description}
                    </p>
                  </div>

                  {/* Footer: Package & Install command */}
                  <div className="mt-3 border-t border-neutral-100 pt-2.5 dark:border-neutral-800/80">
                    <div className="flex items-center justify-between gap-1.5">
                      <div className="flex items-center gap-1 font-mono text-[10px] text-neutral-500 dark:text-neutral-400 truncate">
                        <Terminal className="h-3 w-3 shrink-0 opacity-70" />
                        <span className="truncate">{displayCmd}</span>
                      </div>

                      <button
                        onClick={() => copyCommand(lib)}
                        className={`shrink-0 inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[10px] font-medium transition-all ${
                          isCopied
                            ? "border-emerald-500/50 bg-emerald-50 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-400"
                            : "border-neutral-200 bg-neutral-50 text-neutral-700 hover:border-neutral-300 hover:bg-white dark:border-neutral-800 dark:bg-neutral-800/50 dark:text-neutral-300 dark:hover:border-neutral-700 dark:hover:bg-neutral-800"
                        }`}
                        title="Befehl kopieren"
                      >
                        {isCopied ? (
                          <>
                            <Check className="h-3 w-3 text-emerald-500" />
                            <span>Kopiert</span>
                          </>
                        ) : (
                          <>
                            <Copy className="h-3 w-3 opacity-70" />
                            <span>Kopieren</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
